<template>
  <div class="sponsor-carousel-section">
    <div v-if="displaySponsors.length === 0" class="sponsor-placeholder">
      <div class="placeholder-content">
        <i class="mdi mdi-image-area" style="font-size: 3rem; color: #ccc;"></i>
        <p class="placeholder-text">No sponsors available</p>
      </div>
    </div>
    
    <div v-else class="sponsor-carousel-wrapper" ref="container">
      <div 
        class="sponsor-track" 
        :style="{ 
          transform: `translateX(${translateX}px)`,
          transition: `transform ${transitionDuration}s linear` 
        }"
      >
        <div 
          v-for="(sponsor, index) in displaySponsors" 
          :key="`real-${sponsor.id || index}`"
          class="sponsor-item"
          :style="{ width: itemWidth + 'px' }"
        >
          <div class="sponsor-logo-container">
            <img :src="sponsor.image || sponsor.link" :alt="sponsor.title" class="sponsor-logo" @load="handleImageLoad"/>
          </div>
        </div>

        <div 
          v-for="(sponsor, index) in displaySponsors" 
          :key="`dup-${sponsor.id || index}`"
          class="sponsor-item"
          :style="{ width: itemWidth + 'px' }"
        >
          <div class="sponsor-logo-container">
            <img :src="sponsor.image || sponsor.link" :alt="sponsor.title" class="sponsor-logo" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SponsorCarousel",
  props: {
    autoplayTimeout: {
      type: Number,
      default: 3000 // 3 seconds for sponsors
    },
    sponsors: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      translateX: 0,
      itemWidth: 0,
      currentIndex: 0,
      transitionDuration: 0.8,
      autoplayInterval: null,
      displaySponsors: []
    }
  },
  methods: {
    calculateWidth() {
      const container = this.$refs.container;
      if (container && this.displaySponsors.length > 0) {
        const containerWidth = container.offsetWidth;
        let visibleItems;
        
        if (window.innerWidth <= 480) {
          visibleItems = Math.min(1, this.displaySponsors.length);
        } else if (window.innerWidth <= 768) {
          visibleItems = Math.min(2, this.displaySponsors.length);
        } else if (window.innerWidth <= 1024) {
          visibleItems = Math.min(4, this.displaySponsors.length);
        } else {
          visibleItems = Math.min(5, this.displaySponsors.length);
        }
        
        this.itemWidth = containerWidth / visibleItems;
        this.translateX = -(this.currentIndex * this.itemWidth);
      }
    },
    
    startAutoPlay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
      }
      
      if (this.displaySponsors.length > 1) {
        this.autoplayInterval = setInterval(() => {
          this.nextSlide();
        }, this.autoplayTimeout);
      }
    },

    nextSlide() {
      if (this.displaySponsors.length <= 1) {
        return;
      }
      
      this.currentIndex++;
      this.transitionDuration = 0.8;
      this.translateX = -(this.currentIndex * this.itemWidth);

      if (this.currentIndex >= this.displaySponsors.length) {
        setTimeout(() => {
          this.transitionDuration = 0;
          this.currentIndex = 0;
          this.translateX = 0;
        }, 800);
      }
    },

    setupSponsors() {
      let sponsors = [];
      
      if (this.sponsors && this.sponsors.length > 0) {
        sponsors = this.sponsors;
      } else {
        sponsors = this.getStaticSponsors();
      }
      
      this.displaySponsors = this.prepareSponsorsForCarousel(sponsors);
      
      this.$nextTick(() => {
        this.calculateWidth();
        this.startAutoPlay();
      });
    },

    prepareSponsorsForCarousel(sponsors) {
      if (!sponsors || sponsors.length === 0) {
        return this.getStaticSponsors();
      }

      const minSponsorsNeeded = 6;
      
      if (sponsors.length >= minSponsorsNeeded) {
        return sponsors;
      }
      
      const duplicatedSponsors = [];
      let currentIndex = 0;
      
      while (duplicatedSponsors.length < minSponsorsNeeded) {
        duplicatedSponsors.push({
          ...sponsors[currentIndex],
          id: `${sponsors[currentIndex].id || currentIndex}-${Math.floor(duplicatedSponsors.length / sponsors.length)}`
        });
        currentIndex = (currentIndex + 1) % sponsors.length;
      }
      
      return duplicatedSponsors;
    },

    getStaticSponsors() {
      return [
        { title: 'Isntree', image: require('@/assets/isntree.svg') },
        { title: 'Laneige', image: require('@/assets/laneige.svg') },
        { title: 'Aestura', image: require('@/assets/aestura.svg') },
        { title: 'Isntree', image: require('@/assets/isntree.svg') },
        { title: 'Laneige', image: require('@/assets/laneige.svg') },
        { title: 'Aestura', image: require('@/assets/aestura.svg') }
      ];
    },

    handleImageLoad(event) {
      event.target.style.opacity = '1';
    }
  },
  watch: {
    sponsors: {
      handler() {
        this.setupSponsors();
      },
      immediate: true
    }
  },
  mounted() {
    this.setupSponsors();
    window.addEventListener('resize', this.calculateWidth);
  },
  beforeDestroy() {
    clearInterval(this.autoplayInterval);
    window.removeEventListener('resize', this.calculateWidth);
  }
};
</script>

<style scoped>
.sponsor-carousel-section {
  width: 100%;
  background: transparent;
  overflow: hidden; /* Prevent horizontal overflow */
}

.sponsor-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
  padding: 2rem;
}

.placeholder-content {
  text-align: center;
  color: #9CA3AF;
}

.placeholder-text {
  margin: 0.5rem 0 0 0;
  font-size: 0.9rem;
  font-weight: 500;
}

.sponsor-carousel-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  overflow: hidden; /* Hide the duplicate items */
  position: relative;
  width: 100%;
  box-sizing: border-box; /* Include padding in width calculation */
}

.sponsor-track {
  display: flex;
  align-items: center;
  width: max-content; /* Allow track to be as wide as all items */
}

.sponsor-item {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 10px; /* Reduced padding */
  box-sizing: border-box;
}

.sponsor-logo-container {
  width: 100%;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sponsor-logo {
  max-width: 120px;
  max-height: 120px;
  object-fit: contain;
}

.sponsor-logo:hover {
  opacity: 1;
}

@media (max-width: 768px) {
  .sponsor-carousel-wrapper {
    max-width: 100%;
    padding: 0 5px; /* Reduced padding */
  }
  
  .sponsor-item {
    padding: 0 5px; /* Reduced padding */
  }
  
  .sponsor-logo-container {
    height: 120px;
  }
}

@media (max-width: 480px) {
  .sponsor-carousel-wrapper {
    padding: 0 2px; /* Minimal padding */
  }
  
  .sponsor-item {
    padding: 0 2px; /* Minimal padding */
  }
  
  .sponsor-logo-container {
    height: 40px;
  }
}
</style>